import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Shield, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import CartDrawer from "./CartDrawer";

const linkBase = "px-3 py-2 font-bold uppercase tracking-wider text-sm";
const linkActive = "underline decoration-[3px] decoration-[#FF5A00] underline-offset-[6px]";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { items } = useCart();
  const wl = useWishlist();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const wlCount = wl?.items?.length || 0;

  return (
    <header className="sticky top-0 z-40 bg-paper border-b-4 border-[#1A1A1A]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-3">
        <Link to="/" className="relative inline-flex items-center gap-2 py-2 px-2" data-testid="brand-link">
          <span
            aria-hidden="true"
            className="font-display absolute inset-0 flex items-center justify-center text-[2.45rem] md:text-[3.15rem] uppercase pointer-events-none select-none -rotate-2 whitespace-nowrap"
            style={{
              WebkitTextStroke: "1.5px rgba(255, 90, 0, 0.7)",
              color: "#FFC800",
              letterSpacing: "0.05em",
            }}
          >
            Vintage
          </span>
          <span className="font-display text-2xl md:text-3xl uppercase leading-none relative z-10">Boggy</span>
          <span className="font-sticker text-3xl md:text-4xl text-orange leading-none -rotate-3 relative z-10">&</span>
          <span className="font-display text-2xl md:text-3xl uppercase leading-none relative z-10">Fox</span>
        </Link>
        <nav className="ml-6 hidden md:flex items-center gap-1">
          <NavLink to="/" end className={({isActive}) => `${linkBase} ${isActive ? linkActive : ""}`} data-testid="nav-home">Home</NavLink>
          <NavLink to="/shop" className={({isActive}) => `${linkBase} ${isActive ? linkActive : ""}`} data-testid="nav-shop">Shop</NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" className={({isActive}) => `${linkBase} ${isActive ? linkActive : ""}`} data-testid="nav-admin">Backroom</NavLink>
          )}
        </nav>
        <div className="flex-1" />
        {user && user.role !== "admin" && wlCount > 0 && (
          <button
            onClick={() => nav("/account")}
            className="brut-btn bg-pink text-white px-3 py-2 hidden sm:flex items-center gap-2 relative"
            data-testid="wishlist-button"
            title="Your wishlist"
          >
            <Heart size={18} strokeWidth={3} fill="currentColor" />
            <span className="bg-white text-ink rounded-full px-2 text-xs font-bold border-2 border-[#1A1A1A]" data-testid="wishlist-badge">
              {wlCount}
            </span>
          </button>
        )}
        <button onClick={() => setDrawerOpen(true)} className="brut-btn bg-yellow px-4 py-2 flex items-center gap-2" data-testid="cart-button">
          <ShoppingBag size={18} strokeWidth={3} />
          <span className="hidden sm:inline">Cart</span>
          <span className="bg-ink text-[var(--bg)] rounded-full px-2 text-xs" data-testid="cart-count">{items.length}</span>
        </button>
        {user ? (
          <div className="flex items-center gap-2">
            <button onClick={() => nav(user.role === "admin" ? "/admin" : "/account")} className="brut-btn bg-cyan px-4 py-2 flex items-center gap-2" data-testid="account-button">
              {user.role === "admin" ? <Shield size={18} strokeWidth={3} /> : <User size={18} strokeWidth={3} />}
              <span className="hidden sm:inline">{user.name.split(" ")[0]}</span>
            </button>
            <button onClick={() => { logout(); nav("/"); }} className="brut-btn bg-white px-3 py-2" data-testid="logout-button" title="Log out">
              <LogOut size={18} strokeWidth={3} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/login" className="brut-btn bg-white px-4 py-2 hidden sm:inline-block" data-testid="login-link">Log in</Link>
            <Link to="/signup" className="brut-btn bg-orange text-white px-4 py-2" data-testid="signup-link">Sign up</Link>
          </div>
        )}
      </div>
      <CartDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </header>
  );
}
